import { FocusableSectionProps, LeaveFor } from "@blacknut/spatialnav-sdk/dist";
import React, { useCallback, useEffect, useState } from "react";
import { FocusableSection } from "@blacknut/spatialnav-sdk/dist";
import {
  Entitlement,
  EntitlementCRMInfo,
  Image,
  List,
  Page,
} from "@blacknut/javascript-sdk/dist";
import { useInView } from "react-intersection-observer";
import { useOrientation } from "src/utils/OrientationContext";
import FadeInImage from "src/components/FadeInImage/FadeInImage";
import styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { AppLayout, getEntitlementCRMPriceInfo, passGetImages, State } from "core/redux";
import { logE } from "@blacknut/logging/dist";
import { LOGGING_TAG } from "src/utils/Utils";
import { Button } from "@blacknut/react-sdk";
import { getBackground } from "src/components/TileHero/HeroTilePass";
import { useLayout } from "core/utils";
import { ReactComponent as BuyIcon } from "../../../../assets/dist/ic_pass.svg";
import { ReactComponent as SvgPlaceholder } from "../../../../assets/dist/svgPlaceholder.svg";
import { QRCodeSVG } from "qrcode.react";
import { StorageKey } from "core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

interface HeroOfferViewProps
  extends Pick<FocusableSectionProps, "leaveFor" | "disabled"> {
  list: List;
  leaveFor?: LeaveFor;
  className?: string;
  page?: Page;
}

const HeroOfferView = ({ list, className }: HeroOfferViewProps) => {
  const [ref, inView] = useInView({
    threshold: 0,
    rootMargin: "33% 0px",
  });
  const { orientation } = useOrientation();
  const layout = useLayout();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { passImages } = useSelector((state: State) => state.passState);
  const { crmInfo } = useSelector((state: State) => state.entitlementState);
  const { subscription } = useSelector((state: State) => state.globalState);
  const [qrCodeShown, setQrCodeShown] = useState<boolean>(false);

  const isTVLegacy = localStorage.getItem(StorageKey.TV_LEGACY) === "true";

  const { entitlements } = subscription;
  let entitlement: Entitlement | undefined;
  if (list.entitlement) {
    entitlement = entitlements.find((e) => e.uuid == list.entitlement);
  }
  let myPassImages: Image[] | undefined;
  if (entitlement && entitlement.items?.length) {
    myPassImages = passImages[entitlement.items[0]];
  }
  let entitlementCRMInfo: EntitlementCRMInfo | undefined;
  if (list.entitlement) {
    entitlementCRMInfo = crmInfo[list.entitlement];
  }

  useEffect(() => {
    const init = async () => {
      if (!myPassImages && entitlement?.items?.length) {
        try {
          await passGetImages(entitlement.items[0])(dispatch);
        } catch (e) {
          logE(LOGGING_TAG, "Caught error getting pass images: %o", e);
        }
      }
      if (!entitlementCRMInfo && list.entitlement) {
        try {
          await getEntitlementCRMPriceInfo(list.entitlement)(dispatch);
        } catch (e) {
          logE(LOGGING_TAG, "Caught error getting entilement crm info: %o", e);
        }
      }
    };
    init();
  }, [dispatch, entitlement, entitlementCRMInfo, list.entitlement, myPassImages]);

  const onPurchase = useCallback(() => {
    if (!entitlementCRMInfo) return;
    if (layout === AppLayout.TV) {
      setQrCodeShown(!qrCodeShown);
    } else {
      window.open(entitlementCRMInfo.subscribeLink);
    }
  }, [entitlementCRMInfo, layout, qrCodeShown]);

  const image = getBackground(layout, orientation, myPassImages);
  return (
    <div ref={ref} className={className}>
      {(inView || process.env.NODE_ENV === "test") && (
        <FocusableSection focusKey={list.uuid} className={styles.container}>
          <FadeInImage image={image} className={styles.image} />
          {entitlementCRMInfo?.subscribeLink && (
            <div
              data-testid="svgPlaceholder"
              className={clsx([
                styles.svgPlaceholder,
                qrCodeShown && styles.displayed,
                !isTVLegacy && styles.smoothTransition,
              ])}
            >
              <div>
                <SvgPlaceholder />
                <div
                  dangerouslySetInnerHTML={{
                    __html: t("heroOffer.qrCodeText"),
                  }}
                ></div>
                <QRCodeSVG
                  className={styles.qrCode}
                  value={entitlementCRMInfo?.subscribeLink}
                  marginSize={2}
                />
              </div>
            </div>
          )}
          {entitlementCRMInfo && (
            <div className={styles.content}>
              <div className={styles.title}>{entitlementCRMInfo.heroText}</div>
              {entitlementCRMInfo.heroItems && (
                <ul className={styles.description}>
                  {entitlementCRMInfo.heroItems.map((item) => (
                    <li key={item.text}>
                      {item.picto && <img className={styles.picto} src={item.picto} />}
                      <span dangerouslySetInnerHTML={{ __html: `${item.text}` }} />
                    </li>
                  ))}
                </ul>
              )}

              <Button
                variant="primary"
                className={styles.buyButton}
                onClick={onPurchase}
                onBlur={() => setQrCodeShown(false)}
              >
                <BuyIcon /> {entitlementCRMInfo?.heroButton}
              </Button>

              {entitlementCRMInfo?.legal && (
                <span className={styles.disclaimer}>{entitlementCRMInfo?.legal}</span>
              )}
            </div>
          )}
        </FocusableSection>
      )}
    </div>
  );
};

export default HeroOfferView;
export { HeroOfferView };
